/**
 * Di quali mercati Polymarket ha bisogno ogni categoria di scommessa, letto
 * dalle REGOLE invece che ricopiato.
 *
 * PERCHÉ NON SCRIVERLO A MANO
 * Stesso motivo di `fair-gates.ts`: una mappa ricopiata invecchia in silenzio.
 * Il giorno che una regola nuova compra un Over sotto la categoria `winner`, una
 * tabella scritta a mano continuerebbe a dire "tutto a posto" mentre quella
 * scaletta non può aprire — ed è esattamente l'errore che questo modulo esiste
 * per evitare. La fonte è `strategy-rules.json`, la stessa che il bot valuta.
 *
 * COSA HA FATTO NASCERE QUESTO FILE
 * Il 19 ago 2026 su Águilas Doradas–Llaneros il pannello mostrava "MERCATO OK"
 * in verde. Polymarket per quella partita aveva quotato solo l'esito e il
 * punteggio esatto: il fratello `-more-markets`, dove vivono Over/Under e BTTS,
 * dava 404. L'operatore aveva lasciato **BTTS acceso** aspettandosi che
 * giocasse, e quella scaletta non poteva scattare — il badge diceva "ho trovato
 * l'evento", chi guardava leggeva "ho trovato quello che ti serve".
 */
import { join } from "node:path";
import { BET_CATEGORIES, type BetCategory } from "./types.ts";

/**
 * Le famiglie di mercato che il registro del bot espone. Non la riga esatta
 * (`OU2.5` contro `OU0.5`): quella dipende dal punteggio del momento e non si
 * può sapere prima del fischio d'inizio. La famiglia sì, ed è la domanda a cui
 * il badge deve rispondere — "questa categoria ha un mercato dietro, sì o no?".
 */
export type MarketFamily = "WINNER" | "OU" | "BTTS" | "ES" | "FTS";

export const FAMILY_LABELS: Record<MarketFamily, string> = {
  WINNER: "Esito finale",
  OU: "Over/Under",
  BTTS: "Gol/No Gol",
  ES: "Punteggio esatto",
  FTS: "Prima rete",
};

/** Il nome logico di un ordine → la famiglia del registro che lo serve. */
function familyOf(market: unknown): MarketFamily | null {
  switch (market) {
    case "WINNER": return "WINNER";
    case "BTTS": return "BTTS";
    case "EXACT_SCORE": return "ES";
    case "FTS": return "FTS";
    case "OVER":
    case "UNDER":
    // Un totale di squadra è pur sempre una riga Over/Under: il registro lo
    // scopre insieme agli altri totali, dallo stesso evento fratello.
    case "TEAM_TOTAL":
      return "OU";
    default: return null;
  }
}

/**
 * Famiglia di appartenenza di una chiave del registro (`OU1H0.5` → OU,
 * `ES2-1` → ES). Il registro conia varianti per metà tempo e per squadra
 * (`OU1H1.5`, `TTH1.5`, `BTTS1H`), quindi si guarda il prefisso.
 */
export function familyOfKind(kind: string): MarketFamily | null {
  if (kind === "WINNER") return "WINNER";
  if (kind === "FTS") return "FTS";
  if (kind.startsWith("BTTS")) return "BTTS";
  if (kind.startsWith("ES")) return "ES";
  if (kind.startsWith("OU") || kind.startsWith("TT")) return "OU";
  return null;
}

export type CategoryNeeds = Partial<Record<BetCategory, MarketFamily[]>>;

/**
 * Per ogni categoria, le famiglie che le sue regole ATTIVE comprano.
 *
 * Solo le attive: una regola in `_disabledRules` non apre niente, quindi il suo
 * mercato mancante non è un problema da segnalare. È la differenza fra "non puoi
 * giocare questa categoria" e "c'è una regola spenta che un giorno la userebbe".
 */
export function categoryNeedsFrom(rules: unknown): CategoryNeeds {
  const doc = rules as { rules?: unknown } | null;
  const out: CategoryNeeds = {};
  if (!doc || !Array.isArray(doc.rules)) return out;
  for (const r of doc.rules as Array<{ category?: unknown; orders?: unknown }>) {
    const cat = r?.category;
    if (typeof cat !== "string" || !BET_CATEGORIES.includes(cat as BetCategory)) continue;
    if (!Array.isArray(r.orders)) continue;
    for (const o of r.orders as Array<{ market?: unknown }>) {
      const fam = familyOf(o?.market);
      if (!fam) continue;
      const lista = (out[cat as BetCategory] ??= []);
      if (!lista.includes(fam)) lista.push(fam);
    }
  }
  return out;
}

/** Legge la mappa dal `strategy-rules.json` del bot. Un file illeggibile dà una
 *  mappa vuota: il badge tornerà al verde di prima, che è meno informativo ma
 *  non inventa avvisi. */
export async function readCategoryNeeds(botDir: string): Promise<CategoryNeeds> {
  try {
    return categoryNeedsFrom(await Bun.file(join(botDir, "strategy-rules.json")).json());
  } catch {
    return {};
  }
}
